import React, { useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import FileUpload, { UploadedFile } from '../components/documents/FileUpload';
import DocumentGrid from '../components/documents/DocumentGrid';
import DocumentViewer from '../components/modals/DocumentViewer';
import Button from '../components/ui/Button';
import Card, { CardHeader, CardContent } from '../components/ui/Card';
import { 
  Upload, 
  FileText, 
  Clock, 
  CheckCircle, 
  AlertCircle,
  Download,
  Sparkles,
  Info,
  AlertTriangle,
  XCircle,
  X
} from 'lucide-react';
import { AnimatePresence } from 'framer-motion';
import { apiService } from '../services/api';

interface DocumentsProps {
  onLogout: () => void;
}

const Documents: React.FC<DocumentsProps> = ({ onLogout }) => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<'upload' | 'documents'>('upload');
  const [documents, setDocuments] = useState<UploadedFile[]>([]);
  const [notification, setNotification] = useState<{ type: 'success' | 'info' | 'warning' | 'error', title: string, message: string, details?: any } | null>(null);
  
  // Load existing documents on component mount
  useEffect(() => {
    loadExistingDocuments();
  }, []);

  // Debug: Log documents state changes
  useEffect(() => {
    console.log('📚 Documents state updated:', documents);
  }, [documents]);

  const showNotification = (type: 'success' | 'info' | 'warning' | 'error', title: string, message: string, details?: any) => {
    console.log('🔔 Documents: Setting notification:', { type, title, message, details });
    setNotification({ type, title, message, details });
    // Auto-hide after 30 seconds (longer since it's contextual)
    setTimeout(() => {
      console.log('🔔 Documents: Auto-hiding notification');
      setNotification(null);
    }, 30000);
  };

  const loadExistingDocuments = async () => {
    try {
      const response = await apiService.getUploadedDocuments();
      if (response.success && response.data) {
        // Convert backend document format to frontend format
        const docs: UploadedFile[] = response.data.map((doc: any) => ({
          id: doc.id.toString(),
          file: new File([''], doc.original_name || doc.filename),
          name: doc.original_name || doc.filename,
          size: parseInt(doc.file_size) || 0,
          type: doc.file_type || 'application/pdf',
          status: 'success' as const,
          progress: 100
        }));
        setDocuments(docs);
        console.log('📚 Loaded existing documents:', docs.length);
      }
    } catch (error) {
      console.error('Failed to load existing documents:', error);
    }
  };

  const handleFilesUploaded = useCallback((files: UploadedFile[]) => {
    console.log('📥 handleFilesUploaded called with:', files);
    
    setDocuments(prev => {
      // Check if this is a new upload (file with progress 0 and uploading status)
      const isNewUpload = files.some(file => file.progress === 0 && file.status === 'uploading');
      
      if (isNewUpload) {
        console.log('🆕 New upload detected, adding files to documents');
        // Switch to documents tab after new upload
        setActiveTab('documents');
        // Only add files that don't already exist
        const newFiles = files.filter(file => 
          !prev.some(existing => existing.name === file.name)
        );
        return [...prev, ...newFiles];
      } else {
        console.log('🔄 Progress/status update, merging with existing files');
        console.log('🔍 Looking for files to update...');
        console.log('🔍 Existing docs:', prev.map(d => ({ id: d.id, name: d.name, status: d.status })));
        console.log('🔍 Update files:', files.map(f => ({ id: f.id, name: f.name, status: f.status })));
        
        const updated = prev.map(existingDoc => {
          // Find the updated file by name (most reliable)
          const updatedFile = files.find(f => f.name === existingDoc.name);
          
          if (updatedFile) {
            console.log(`📝 Updating ${existingDoc.name}: ID ${existingDoc.id} → ${updatedFile.id}, status ${existingDoc.status} → ${updatedFile.status}`);
            return { ...existingDoc, ...updatedFile }; // Merge to preserve any local state
          }
          return existingDoc;
        });
        
        // Add any new files that weren't in the existing list
        const newFiles = files.filter(file => 
          !prev.some(existing => existing.name === file.name)
        );
        
        const result = [...updated, ...newFiles];
        console.log('📊 Updated documents state:', result.map(d => ({ id: d.id, name: d.name, status: d.status })));
        return result;
      }
    });
  }, []);

  const [viewerOpen, setViewerOpen] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState<UploadedFile | null>(null);

  const handleViewDocument = useCallback((document: UploadedFile) => {
    console.log('Viewing document:', document.name);
    setSelectedDocument(document);
    setViewerOpen(true);
  }, []);

  const handleDeleteDocument = useCallback(async (documentId: string) => {
    const document = documents.find(doc => doc.id === documentId);
    const documentName = document?.name || 'this document';
    
    // Confirm deletion
    if (!confirm(`Are you sure you want to delete "${documentName}"?\n\nThis action cannot be undone and will remove the document from all systems including the vector database.`)) {
      return;
    }
    
    try {
      // First remove from UI optimistically for better UX
      setDocuments(prev => prev.filter(doc => doc.id !== documentId));
      
      // Then call backend to actually delete
      const response = await apiService.deleteDocument(documentId);
      
      if (!response.success) {
        console.error('Failed to delete document:', response);
        alert('Failed to delete document. Please try again.');
        // Reload documents to restore state if delete failed
        await loadExistingDocuments();
      } else {
        console.log('✅ Document deleted successfully');
        // Show success message
        alert(`Document "${documentName}" has been deleted successfully.`);
      }
    } catch (error) {
      console.error('❌ Error deleting document:', error);
      alert('An error occurred while deleting the document. Please try again.');
      // Reload documents to restore state if delete failed  
      await loadExistingDocuments();
    }
  }, [documents]);

  const handleDownloadDocument = useCallback((doc: UploadedFile) => {
    // In a real app, this would trigger a download
    console.log('Downloading document:', doc.name);
    
    // Create a download link for demo purposes
    const url = URL.createObjectURL(doc.file);
    const a = document.createElement('a');
    a.href = url;
    a.download = doc.name;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }, []);

  // Test backend connection
  const testBackend = async () => {
    try {
      console.log('🧪 Testing backend connection...');
      const response = await apiService.healthCheck();
      console.log('🏥 Backend health check:', response);
      alert(`Backend Status: ${response.success ? '✅ Healthy' : '❌ Unhealthy'}\n\nResponse: ${JSON.stringify(response, null, 2)}`);
    } catch (error) {
      console.error('❌ Backend test failed:', error);
      alert(`Backend Test Failed: ${error}`);
    }
  };

  // Calculate statistics
  const totalDocuments = documents.length;
  const readyDocuments = documents.filter(doc => doc.status === 'success').length;
  const errorDocuments = documents.filter(doc => doc.status === 'error').length;
  const uploadingDocuments = documents.filter(doc => doc.status === 'uploading').length;

  const tabs = [
    { id: 'upload', label: 'Upload Documents', icon: Upload, count: null },
    { id: 'documents', label: 'My Documents', icon: FileText, count: totalDocuments }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-blue-50">
      {/* Premium Header */}
      <div className="relative bg-gradient-to-r from-blue-600 via-blue-700 to-indigo-700 overflow-hidden">
        {/* Background decoration */}
        <div className="absolute inset-0">
          <div className="absolute top-0 left-0 w-72 h-72 bg-blue-500 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-blob"></div>
          <div className="absolute top-0 right-0 w-72 h-72 bg-indigo-500 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-blob animation-delay-2000"></div>
          <div className="absolute -bottom-8 left-20 w-72 h-72 bg-purple-500 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-blob animation-delay-4000"></div>
        </div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-20">
            <div className="flex items-center space-x-4">
              <div className="p-3 bg-white/20 rounded-xl backdrop-blur-sm">
                <FileText className="w-8 h-8 text-white" />
              </div>
              <div>
                <h1 className="text-3xl font-bold text-white">Document Management</h1>
                <p className="text-blue-100 text-sm">AI-Powered Trust Document Analysis</p>
              </div>
            </div>
            
            <div className="flex items-center space-x-4">
              <Button 
                variant="outline" 
                onClick={testBackend}
                className="bg-white/10 border-white/20 text-white hover:bg-white/20 backdrop-blur-sm"
              >
                🧪 Test Backend
              </Button>
              <Button 
                variant="outline" 
                onClick={onLogout}
                className="bg-white/10 border-white/20 text-white hover:bg-white/20 backdrop-blur-sm"
              >
                Sign Out
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Premium Statistics Cards */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-12 -mt-8 relative z-10"
        >
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.9 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={{ duration: 0.5, delay: 0.1 }}
          >
            <Card className="bg-white/80 backdrop-blur-sm border-0 shadow-xl hover:shadow-2xl transition-all duration-300 hover:-translate-y-1">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <div className="p-3 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl shadow-lg">
                      <FileText className="w-6 h-6 text-white" />
                    </div>
                    <div className="ml-4">
                      <p className="text-sm font-medium text-gray-600">Total Documents</p>
                      <p className="text-3xl font-bold text-gray-900">{totalDocuments}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-2xl font-bold text-blue-600">📊</div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.9 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            <Card className="bg-white/80 backdrop-blur-sm border-0 shadow-xl hover:shadow-2xl transition-all duration-300 hover:-translate-y-1">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <div className="p-3 bg-gradient-to-br from-green-500 to-green-600 rounded-xl shadow-lg">
                      <CheckCircle className="w-6 h-6 text-white" />
                    </div>
                    <div className="ml-4">
                      <p className="text-sm font-medium text-gray-600">Ready</p>
                      <p className="text-3xl font-bold text-gray-900">{readyDocuments}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-2xl font-bold text-green-600">✅</div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.9 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={{ duration: 0.5, delay: 0.3 }}
          >
            <Card className="bg-white/80 backdrop-blur-sm border-0 shadow-xl hover:shadow-2xl transition-all duration-300 hover:-translate-y-1">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <div className="p-3 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl shadow-lg">
                      <Clock className="w-6 h-6 text-white" />
                    </div>
                    <div className="ml-4">
                      <p className="text-sm font-medium text-gray-600">Processing</p>
                      <p className="text-3xl font-bold text-gray-900">{uploadingDocuments}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-2xl font-bold text-blue-600">⏳</div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.9 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={{ duration: 0.5, delay: 0.4 }}
          >
            <Card className="bg-white/80 backdrop-blur-sm border-0 shadow-xl hover:shadow-2xl transition-all duration-300 hover:-translate-y-1">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <div className="p-3 bg-gradient-to-br from-red-500 to-red-600 rounded-xl shadow-lg">
                      <AlertCircle className="w-6 h-6 text-white" />
                    </div>
                    <div className="ml-4">
                      <p className="text-sm font-medium text-gray-600">Errors</p>
                      <p className="text-3xl font-bold text-gray-900">{errorDocuments}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-2xl font-bold text-red-600">⚠️</div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        </motion.div>

                {/* Contextual Notification - Appears below upload area */}
        <AnimatePresence>
          {notification && (
            <motion.div
              initial={{ opacity: 0, y: -10, scale: 0.98 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -10, scale: 0.98 }}
              transition={{ duration: 0.3, ease: "easeOut" }}
              className="mb-6"
            >
              <div className={`
                bg-white border rounded-xl shadow-sm p-4 max-w-2xl mx-auto
                ${notification.type === 'success' ? 'border-green-200 bg-green-50/50' :
                  notification.type === 'info' ? 'border-blue-200 bg-blue-50/50' :
                  notification.type === 'warning' ? 'border-yellow-200 bg-yellow-50/50' :
                  'border-red-200 bg-red-50/50'}
              `}>
                <div className="flex items-start">
                  <div className={`
                    flex-shrink-0 w-6 h-6 rounded-full mr-4 mt-0.5 flex items-center justify-center
                    ${notification.type === 'success' ? 'bg-green-500' :
                      notification.type === 'info' ? 'bg-blue-500' :
                      notification.type === 'warning' ? 'bg-yellow-500' :
                      'bg-red-500'}
                  `}>
                    {notification.type === 'success' && <CheckCircle className="w-4 h-4 text-white" />}
                    {notification.type === 'info' && <Info className="w-4 h-4 text-white" />}
                    {notification.type === 'warning' && <AlertTriangle className="w-4 h-4 text-white" />}
                    {notification.type === 'error' && <XCircle className="w-4 h-4 text-white" />}
                  </div>
                  
                  <div className="flex-1 min-w-0">
                    <h4 className="text-base font-semibold text-gray-900 mb-1">
                      {notification.title}
                    </h4>
                    <p className="text-gray-700 text-sm leading-relaxed">
                      {notification.message}
                    </p>
                    
                    {/* Show details if available */}
                    {notification.details && (
                      <div className="mt-3 p-3 bg-white/70 rounded-lg border border-white/50">
                        <div className="text-xs text-gray-600">
                          <strong>Document ID:</strong> {notification.details.documentId} | 
                          <strong> Status:</strong> {notification.details.status} | 
                          <strong> Chunks:</strong> {notification.details.chunks} | 
                          <strong> Uploaded:</strong> {notification.details.uploadDate}
                        </div>
                      </div>
                    )}
                  </div>
                  
                  <button
                    onClick={() => setNotification(null)}
                    className="ml-4 flex-shrink-0 p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Tabs */}
        <div className="mb-8">
          <div className="border-b border-gray-200">
            <nav className="-mb-px flex space-x-8">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id as any)}
                  className={`py-2 px-1 border-b-2 font-medium text-sm flex items-center space-x-2 ${
                    activeTab === tab.id
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <tab.icon className="w-4 h-4" />
                  <span>{tab.label}</span>
                  {tab.count !== null && (
                    <span className="bg-gray-100 text-gray-900 py-0.5 px-2 rounded-full text-xs">
                      {tab.count}
                    </span>
                  )}
                </button>
              ))}
            </nav>
          </div>
        </div>

        {/* Tab Content */}
        <AnimatePresence mode="wait">
          {activeTab === 'upload' && (
            <motion.div
              key="upload"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.3 }}
            >
              
              <FileUpload
                onFilesUploaded={handleFilesUploaded}
                onShowNotification={showNotification}
                maxFiles={20}
                maxFileSize={100}
                acceptedTypes={['.pdf', '.docx', '.doc', '.txt']}
              />
            </motion.div>
          )}

          {activeTab === 'documents' && (
            <motion.div
              key="documents"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.3 }}
            >
              <DocumentGrid
                documents={documents}
                onViewDocument={handleViewDocument}
                onDeleteDocument={handleDeleteDocument}
                onDownloadDocument={handleDownloadDocument}
              />
            </motion.div>
          )}
        </AnimatePresence>

        {/* Quick Actions */}
        {documents.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mt-12"
          >
            <Card>
              <CardHeader>
                <h3 className="text-lg font-semibold text-gray-900">Quick Actions</h3>
              </CardHeader>
              <CardContent>
                <div className="flex flex-wrap gap-4">
                  <Button
                    variant="outline"
                    onClick={() => setActiveTab('upload')}
                    leftIcon={<Upload className="w-4 h-4" />}
                  >
                    Upload More Documents
                  </Button>
                  
                  <Button
                    onClick={() => navigate('/chat')}
                    leftIcon={<Sparkles className="w-4 h-4" />}
                  >
                    Ask Your Documents
                  </Button>
                  
                  <Button
                    variant="outline"
                    onClick={() => {
                      // In a real app, this would export document list
                      alert('This would export a list of all documents.');
                    }}
                    leftIcon={<Download className="w-4 h-4" />}
                  >
                    Export Document List
                  </Button>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        )}
      </div>
      
      {/* Document Viewer Modal */}
      <DocumentViewer
        isOpen={viewerOpen}
        onClose={() => setViewerOpen(false)}
        documentId={selectedDocument?.id || ''}
        documentName={selectedDocument?.name || ''}
      />
    </div>
  );
};

export default Documents;
